/**
 * Post Edit Handler
 * Pure event handlers for post edit functionality
 * Handles SNS link management and preview generation
 */

import { SNSLinksUtils } from "../../sns/links/sns-links-utils";
import { SNSPostHandler } from "../../sns/post/sns-post-handler";
import { SNSPostRender } from "../../sns/post/sns-post-render";
import { SNSPostUtils } from "../../sns/post/sns-post-utils";
import { PostSharedPreviewTabUtils } from "../shared/post-shared-preview-tab-utils";
import { postEditForm } from "./post-edit-form";

type PostEditElementValue = Document | Element | string | number | boolean | null | undefined;

interface PostEditElements {
    formContainer?: Document | HTMLElement | null;
    form?: HTMLElement | null;
    snsLinksList?: HTMLElement | null;
    snsAddBtn?: HTMLButtonElement | null;
    youtubeSection?: HTMLElement | null;
    youtubeContainer?: HTMLElement | null;
    snsContainer?: HTMLElement | null;
    ogpContainer?: HTMLElement | null;
    contentTextarea?: HTMLTextAreaElement | null;
    previewRoot?: HTMLElement | null;
    previewRichText?: Element | null;
    snsTabContainer?: HTMLElement | null;
    ogpTabContainer?: HTMLElement | null;
    imageManager?: HTMLElement | null;
    videoManager?: HTMLElement | null;
    [key: string]: PostEditElementValue;
}

class PostEditHandler {
    public elements: PostEditElements;
    public _snsHandler: SNSPostHandler;

    public constructor() {
        this.elements = {};

        this._snsHandler = new SNSPostHandler({
            getSnsLinksList: () =>
                this.elements?.snsLinksList instanceof HTMLElement ? this.elements.snsLinksList : null,
            updatePreviews: () => this.updatePreviews(),
        });
    }

    public handleSnsAdd(event: Event): void {
        this._snsHandler.handleSnsAdd(event);
    }

    public handleSnsInput(event: Event): void {
        this._snsHandler.handleSnsInput(event);
    }

    public handleSnsPreviewActivate(event: Event): void {
        this._snsHandler.handleSnsPreviewActivate(event);
    }

    public updatePreviews(): void {
        const urls = SNSPostUtils.getNormalizedSnsUrlsFromList(this.elements?.snsLinksList || null);

        SNSPostRender.clearSnsPreviewContainers(this.elements);
        SNSPostRender.renderSnsPreviewTiles({ elements: this.elements, urls });

        this.updatePreviewContent?.();
    }

    public clearPreviews(): void {
        SNSPostRender.clearSnsPreviewContainers(this.elements);
    }

    public updatePreviewContent(): void {
        const formRoot = this.elements.formContainer instanceof HTMLElement ? this.elements.formContainer : null;

        const previewRootCandidate =
            this.elements.previewRoot ||
            (formRoot ? postEditForm.getPreviewRoot(formRoot) : null) ||
            postEditForm.getPreviewRoot(document);
        if (!(previewRootCandidate instanceof HTMLElement)) return;
        if (!(formRoot instanceof HTMLElement)) return;
        const previewRoot = previewRootCandidate;

        const { html } = this.getEditorContent();
        const inputs = this.elements.snsLinksList ? postEditForm.getSnsLinkInputs(this.elements.snsLinksList) : [];
        const snsUrls = SNSLinksUtils.normalizeSnsLinks(
            Array.from(inputs)
                .map((input) => ((input instanceof HTMLInputElement ? input.value : "") || "").trim())
                .filter(Boolean),
        );

        PostSharedPreviewTabUtils.updatePreviewTab({
            formRoot,
            previewRoot,
            html,
            snsUrls,
        });

        this.updateOgpPreviewInTab();
    }

    public updateOgpPreviewInTab(): void {
        const formRoot = this.elements.formContainer instanceof HTMLElement ? this.elements.formContainer : null;
        const previewTabContainer = postEditForm.getOgpPreviewTabWithin(
            this.elements.ogpTabContainer || (formRoot ? postEditForm.getOgpPreviewTab(formRoot) : null) || document,
        );
        if (!previewTabContainer) return;

        const contentTextarea =
            this.elements.contentTextarea instanceof HTMLTextAreaElement
                ? this.elements.contentTextarea
                : postEditForm.getContentTextareaWithin(formRoot || document);
        if (!contentTextarea) return;

        PostSharedPreviewTabUtils.updateOgpPreviewInTab({
            contentTextarea,
            previewTabContainer,
        });
    }

    public getEditorContent(): { html: string; contentText: string } {
        let html = "";
        let contentText = "";

        const formRoot = this.elements.formContainer || document;
        const quillRoot = postEditForm.getWysiwygContent(formRoot);
        const contentTextarea = this.elements.contentTextarea;

        if (quillRoot && quillRoot.innerHTML.trim()) {
            html = quillRoot.innerHTML;
            contentText = quillRoot.textContent || quillRoot.innerText || "";
        } else if (contentTextarea instanceof HTMLTextAreaElement && contentTextarea.value.trim()) {
            contentText = contentTextarea.value;
            html = contentText.replace(/\n/g, "<br>");
        }

        return { html, contentText };
    }
}

export { PostEditHandler };
export type { PostEditElements };
